import React, { useState, useEffect, useRef } from "react"
import { useLang } from "hooks/useLang"
import Button from "components/Form/Button"

interface FadeOutEffectProps {
    children: React.ReactNode
    fadeStartAt?: number
    fadeHeight?: number
    externalLink?: string
}

const FadeOutEffect: React.FC<FadeOutEffectProps> = ({
    children,
    fadeStartAt = 400,
    fadeHeight = 200,
    externalLink
}) => {
    const { t } = useLang()
    const [isExpanded, setIsExpanded] = useState(false)
    const [showViewMore, setShowViewMore] = useState(false)
    const [contentHeight, setContentHeight] = useState(0)
    const contentRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight
            setContentHeight(scrollHeight)
            setShowViewMore(scrollHeight > fadeStartAt + fadeHeight)
        }
    }, [children, fadeStartAt, fadeHeight])

    const toggleViewMore = () => {
        if (externalLink) {
            window.open(externalLink, "_blank")
            return
        }
        const newExpandedState = !isExpanded
        setIsExpanded(newExpandedState)

        // Scroll to start of content when collapsing
        if (!newExpandedState && containerRef.current) {
            // Add a small delay to let the collapse animation start
            setTimeout(() => {
                const headerOffset = 120 // Adjust with header height
                const elementPosition =
                    containerRef.current!.getBoundingClientRect().top
                const offsetPosition =
                    elementPosition + window.pageYOffset - headerOffset

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                })
            }, 100)
        }
    }

    const fadeEndAt = fadeStartAt + fadeHeight

    //  calculate the gradient for the fade effect
    const maskGradient = `linear-gradient(
        to bottom,
        rgba(0,0,0,1) ${fadeStartAt}px,
        rgba(0,0,0,0) ${fadeEndAt}px
    )`

    let maxHeight = isExpanded ? `${contentHeight}px` : `${fadeEndAt}px`

    return (
        <div className="relative" ref={containerRef}>
            <div
                ref={contentRef}
                className="relative overflow-hidden"
                style={{
                    maxHeight,
                    transition: "max-height 0.5s ease",
                    maskImage:
                        showViewMore && !isExpanded ? maskGradient : "none",
                    WebkitMaskImage:
                        showViewMore && !isExpanded ? maskGradient : "none",
                    maskSize: "100% 100%",
                    WebkitMaskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "top",
                    WebkitMaskPosition: "top",
                }}
            >
                {children}
            </div>
            {showViewMore && (
                <Button
                    className={`bg-transparent border border-white rounded-md dark:text-white text-black opacity-60 cursor-pointer capitalize-first text-center px-5 text-xs py-1.5 hover:bg-transparent ${isExpanded ? "mt-3" : "mt-1.5"
                        }`}
                    onClick={toggleViewMore}
                >
                    {t(isExpanded ? "read_less" : "read_more")}{" "}
                    {isExpanded ? "−" : "+"}
                </Button>
            )}
        </div>
    )
}

export default FadeOutEffect
